import {isDeepEqual} from '@raison/toolbox';
import * as Either from 'effect/Either';
import {isFunction} from 'effect/Function';
import {isRecord} from 'effect/Predicate';
import * as Schema from 'effect/Schema';
import {SetStateAction, useCallback, useMemo} from 'react';
import {
    createSearchParams,
    NavigateFunction,
    NavigateOptions,
    SetURLSearchParams,
    useLocation,
    useNavigate,
} from 'react-router';
import {useNonReactiveCallback} from '~/library/ui-kit';

export function useSearchParamsToolsClosure(navigate: NavigateFunction) {
    return useMemo(
        () => ({
            useSearchParams: () => useSearchParamsImpl(navigate),
            useSearchParam: (key: string) => useSearchParamImpl(navigate, key),
            useSearchParamStructured: <T extends object>(key: string, options: SearchParamStructureDef<T>) =>
                useSearchParamStructuredImpl(navigate, key, options),
        }),
        [navigate]
    );
}

export function useSearchParams() {
    return useSearchParamsImpl(useNavigate());
}
useSearchParams.withNavigate = useSearchParamsImpl;

export function useSearchParam<T extends string = string>(key: string) {
    return useSearchParamImpl<T>(useNavigate(), key);
}
useSearchParam.withNavigate = useSearchParamImpl;

export function useSearchParamStructured<T extends object>(key: string, options: SearchParamStructureDef<T>) {
    return useSearchParamStructuredImpl(useNavigate(), key, options);
}
useSearchParamStructured.withNavigate = useSearchParamStructuredImpl;

function useSearchParamsImpl(navigate: NavigateFunction): [URLSearchParams, SetURLSearchParams] {
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const setSearchParams = useCallback<SetURLSearchParams>(
        (nextInit, navigateOptions) => {
            if (nextInit instanceof Function) {
                nextInit = nextInit(new URLSearchParams(searchParams));
            }
            navigate('?' + createSearchParams(nextInit), navigateOptions);
        },
        [navigate, searchParams]
    );

    return [searchParams, setSearchParams];
}

interface SetParam<T extends string> {
    (value: SetStateAction<T | (string & {}) | undefined>, options?: NavigateOptions): void;
}
function useSearchParamImpl<T extends string = string>(
    navigate: NavigateFunction,
    key: string
): [T | (string & {}) | undefined, SetParam<T>] {
    const [searchParams, setSearchParams] = useSearchParamsImpl(navigate);

    const value = useMemo(() => searchParams.get(key) ?? undefined, [key, searchParams]);

    const setValue = useMemo(() => {
        const setParam: SetParam<T> = (update, options) => {
            setSearchParams(instance => {
                if (update instanceof Function) {
                    update = update(instance.get(key) ?? undefined);
                }
                if (update === undefined) {
                    instance.delete(key);
                } else {
                    instance.set(key, update);
                }
                return instance;
            }, options);
        };

        return setParam;
    }, [key, setSearchParams]);

    return [value, setValue];
}

export type SearchParamStructureDef<T> = {
    fallback: NoInfer<T>;
    decode: (value: object) => T;
    encode?: (value: T) => string;
};

export type SetStructuredParam<T> = (
    value: T | undefined | ((current: T) => T | undefined),
    options?: NavigateOptions
) => void;

const SearchParamReset = Symbol('SearchParamReset');

function useSearchParamStructuredImpl<T extends object>(
    navigate: NavigateFunction,
    key: string,
    {decode, encode = JSON.stringify, fallback}: SearchParamStructureDef<T>
): [T, SetStructuredParam<T>] {
    const [rawValue, setRawValue] = useSearchParamImpl(navigate, key);

    if (import.meta.env.DEV) {
        // isomorphism check
        const originalEncode = encode;
        encode = value => {
            const encoded = originalEncode(value);
            const decoded = decode(JSON.parse(encoded));
            if (!isDeepEqual(decoded, value)) {
                throw new Error('Search params codec isomorphism violated');
            }
            return encoded;
        };
    }

    const getParsedValue = useCallback(
        (rawValue: string | undefined) => {
            if (rawValue === undefined) {
                return fallback;
            }
            try {
                return decode(JSON.parse(rawValue));
            } catch (err) {
                reportError(err);
                return SearchParamReset;
            }
        },
        [decode, fallback]
    );

    const parsedValue = useMemo(() => getParsedValue(rawValue), [getParsedValue, rawValue]);
    const value = parsedValue === SearchParamReset ? fallback : parsedValue;

    const setValue = useCallback<SetStructuredParam<T>>(
        (update, options) => {
            setRawValue(currentRaw => {
                let current = getParsedValue(currentRaw);
                if (current === SearchParamReset) {
                    current = fallback;
                }
                if (update instanceof Function) {
                    update = update(current);
                }
                return update === undefined ? undefined : encode(update);
            }, options);
        },
        [setRawValue, decode, encode, fallback]
    );

    return [value, setValue];
}

type SetLocationStateOpt = Omit<NavigateOptions, 'state'>;
type SetLocationState<T> = (action: T | ((value: T) => T), options?: SetLocationStateOpt) => void;
type SetLocationStateNoFallback<T> = (action: T | ((value: T | undefined) => T), options?: SetLocationStateOpt) => void;

type useLocationState = {
    <T>(key: string, schema: Schema.Schema<T>): [state: T | undefined, setState: SetLocationStateNoFallback<T>];
    <T>(key: string, schema: Schema.Schema<T>, fallback: T): [state: T, setState: SetLocationState<T>];
};

/**
 * Hook that allows to read and write to location.state almost like regular useState
 * Use with caution; there can be problems with location.state when few setState called at the same react tick
 * @see https://jira.raison.digital/browse/RAISON-6743
 */
export const useLocationState: useLocationState = <T>(
    key: string,
    schema: Schema.Schema<T>,
    fallback?: T
): [state: T | undefined, setState: SetLocationStateNoFallback<T> | SetLocationState<T>] => {
    const codec = {
        decodeState: Schema.decodeUnknownEither(Schema.Struct({[key]: schema})),
        encodeValue: Schema.encodeSync(schema),
    };

    const navigate = useNavigate();
    const location = useLocation();

    const parseLocationState = (raw: unknown) => {
        if (!isRecord(raw) || !(key in raw)) {
            // if state is not object or has no key for this state -
            // getting a parse error is expected, so we don't attempt to parse
            return fallback;
        }
        const parsed = codec.decodeState(raw);
        if (Either.isRight(parsed)) {
            return parsed.right[key];
        }
        if (import.meta.env.DEV) {
            reportError(new Error("Couldn't parse location.state", {cause: parsed.left}));
        }
        return fallback;
    };

    const state = parseLocationState(location.state);

    const setState = useNonReactiveCallback<SetLocationStateNoFallback<T>>((value, options) => {
        if (isFunction(value)) {
            value = value(parseLocationState(location.state) ?? fallback);
        }

        if (value === undefined) {
            const {[key]: _, ...rest} = location.state;
            navigate(location, {...options, state: rest});
        } else {
            navigate(location, {...options, state: {...location.state, [key]: codec.encodeValue(value)}});
        }
    });

    return [state, setState];
};
